import { z } from 'zod';
import { ProgressSchema, type Progress, type TrainingRecord } from '../progress/schema.ts';
import { SettingsSchema, type Settings } from '../settings/schema.ts';

/**
 * Cross-save (milestone 5): the settings and the progress of a player, as the two documents the
 * database stores (`user_documents`). The merge rules live here, in plain functions, so that they
 * are the same on every device and can be tested without a network.
 *
 * - **Settings** merge key by key, the newest change winning: each synced setting carries the time
 *   it last changed on the device that changed it. What is left out is what belongs to the device,
 *   not the player: the graphics (a phone and a gaming PC want different ones) and whether the
 *   first-run pages were seen.
 * - **Progress** merges by the best of both: the best time and stars of every training record, the
 *   union of the Tutorial chapters. It needs no clocks, and merging twice changes nothing.
 */

/** The settings that follow the player from device to device. */
export const SYNCED_SETTINGS = [
  'language',
  'uiScale',
  'audio',
  'game',
  'accessibility',
  'controls',
  'notifications',
] as const;
export type SyncedSetting = (typeof SYNCED_SETTINGS)[number];

export const SettingsDocSchema = z.object({
  v: z.literal(1),
  values: z.record(z.string(), z.unknown()),
  /** Milliseconds since the epoch of the last change of each setting. */
  stamps: z.record(z.string(), z.number()),
});
export type SettingsDoc = z.infer<typeof SettingsDocSchema>;

export const ProgressDocSchema = z.object({ v: z.literal(1), progress: ProgressSchema });
export type ProgressDoc = z.infer<typeof ProgressDocSchema>;

/** JSON with the keys in order, so that equal values give equal text. */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const keys = Object.keys(value).sort();
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`)
    .join(',')}}`;
}

export const sameValue = (a: unknown, b: unknown): boolean =>
  stableStringify(a) === stableStringify(b);

// ---- Settings -------------------------------------------------------------------------------

export const emptySettingsDoc = (): SettingsDoc => ({ v: 1, values: {}, stamps: {} });

/**
 * The document for these settings. A setting that is not in `previous`, or has a different value
 * there, is stamped `now`; one that is the same keeps the stamp it had.
 */
export function settingsDocFrom(
  settings: Settings,
  previous: SettingsDoc | null,
  now: number,
): SettingsDoc {
  const values: Record<string, unknown> = {};
  const stamps: Record<string, number> = {};
  for (const key of SYNCED_SETTINGS) {
    values[key] = settings[key];
    const unchanged =
      previous !== null && key in previous.values && sameValue(previous.values[key], settings[key]);
    stamps[key] = unchanged ? (previous.stamps[key] ?? 0) : now;
  }
  return { v: 1, values, stamps };
}

/**
 * The document of a device that has never synced. What differs from the defaults is something the
 * player chose (stamped `now`); the rest has no age (0) and gives way to whatever the cloud holds,
 * so a fresh install does not overwrite a player's settings with its defaults.
 */
export function initialSettingsDoc(
  settings: Settings,
  defaults: Settings,
  now: number,
): SettingsDoc {
  const values: Record<string, unknown> = {};
  const stamps: Record<string, number> = {};
  for (const key of SYNCED_SETTINGS) {
    values[key] = settings[key];
    stamps[key] = sameValue(settings[key], defaults[key]) ? 0 : now;
  }
  return { v: 1, values, stamps };
}

/** Key by key, the newest stamp wins; on a tie the value that sorts first, the same on both sides. */
export function mergeSettingsDocs(a: SettingsDoc, b: SettingsDoc): SettingsDoc {
  const values: Record<string, unknown> = {};
  const stamps: Record<string, number> = {};
  const keys = new Set([...Object.keys(a.values), ...Object.keys(b.values)]);
  for (const key of keys) {
    const inA = key in a.values;
    const inB = key in b.values;
    const sa = a.stamps[key] ?? 0;
    const sb = b.stamps[key] ?? 0;
    let pick: 'a' | 'b';
    if (inA && !inB) pick = 'a';
    else if (inB && !inA) pick = 'b';
    else if (sa !== sb) pick = sa > sb ? 'a' : 'b';
    else pick = stableStringify(a.values[key]) <= stableStringify(b.values[key]) ? 'a' : 'b';
    values[key] = pick === 'a' ? a.values[key] : b.values[key];
    stamps[key] = pick === 'a' ? sa : sb;
  }
  return { v: 1, values, stamps };
}

/** The settings with the synced ones taken from the document. A value that does not parse is left as it was. */
export function applySettingsDoc(settings: Settings, doc: SettingsDoc): Settings {
  const next: Record<string, unknown> = { ...settings };
  for (const key of SYNCED_SETTINGS) {
    if (!(key in doc.values)) continue;
    const parsed = SettingsSchema.shape[key].safeParse(doc.values[key]);
    if (parsed.success) next[key] = parsed.data;
  }
  return SettingsSchema.parse(next);
}

export function parseSettingsDoc(raw: unknown): SettingsDoc | null {
  const parsed = SettingsDocSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

// ---- Progress -------------------------------------------------------------------------------

function mergeRecord(a: TrainingRecord, b: TrainingRecord): TrainingRecord {
  return {
    bestMs: Math.min(a.bestMs, b.bestMs),
    stars: Math.max(a.stars, b.stars),
    plays: Math.max(a.plays, b.plays),
  };
}

/** The best of both: quickest times, most stars, most plays, every chapter either has done. */
export function mergeProgress(a: Progress, b: Progress): Progress {
  const training: Record<string, TrainingRecord> = {};
  for (const key of new Set([...Object.keys(a.training), ...Object.keys(b.training)])) {
    const x = a.training[key];
    const y = b.training[key];
    training[key] = x && y ? mergeRecord(x, y) : (x ?? (y as TrainingRecord));
  }
  return ProgressSchema.parse({ training, tutorial: [...a.tutorial, ...b.tutorial] });
}

export const progressDocFrom = (progress: Progress): ProgressDoc => ({ v: 1, progress });

export function parseProgressDoc(raw: unknown): Progress | null {
  const parsed = ProgressDocSchema.safeParse(raw);
  return parsed.success ? parsed.data.progress : null;
}
